import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { Button, Skeleton, Typography } from 'antd';
import { Sparkles, Flame, Clock, CalendarDays, AlertTriangle } from 'lucide-react';

import type { ManagerBriefing } from '../../features/dashboard/api/dashboard.client.js';
import { ErrorState } from '../../shared/ui/ErrorState.js';

const { Text } = Typography;

export interface MorningBriefingBannerProps {
  briefing?: ManagerBriefing;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  onRunAi?: () => void;
  /** Disables the AI button when there is nothing to review (all priorities empty). */
  runAiDisabled?: boolean;
}

interface BannerMetric {
  key: string;
  label: string;
  count: number;
  color: string;
  icon: typeof Flame;
}

/**
 * «Брифинг дня» — Warm Precision D: одна hairline-полоса, счётчики с
 * табличными цифрами, amber-CTA. Разворачивается в расширенную строку метрик.
 */
export function MorningBriefingBanner({
  briefing,
  loading = false,
  error = false,
  onRetry,
  onRunAi,
  runAiDisabled = false,
}: MorningBriefingBannerProps): ReactElement {
  const { t } = useTranslation();
  // Compact by default: the panel is a one-line summary of the day, and the
  // full metric row is one click away.
  const [collapsed, setCollapsed] = useState(true);

  if (loading) {
    return (
      <div
        data-testid="morning-briefing-banner"
        className="crm-radius-m border p-3.5"
        style={{
          borderColor: 'var(--ant-color-border-secondary, #e7e5e4)',
          background: 'var(--ant-color-bg-container, #fffdfa)',
        }}
      >
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: 220 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="morning-briefing-banner">
        <ErrorState
          title={t('crm.dashboard.morning_briefing.error')}
          {...(onRetry ? { onRetry } : {})}
        />
      </div>
    );
  }

  if (!briefing) {
    return (
      <div
        data-testid="morning-briefing-banner"
        className="flex items-center gap-3 crm-radius-m border px-3.5 py-2.5"
        style={{
          borderColor: 'var(--ant-color-border-secondary, #e7e5e4)',
          background: 'var(--ant-color-bg-container, #fffdfa)',
        }}
      >
        <Sparkles size={16} style={{ color: 'var(--ant-color-primary)', flexShrink: 0 }} />
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('crm.dashboard.morning_briefing.empty')}
        </Text>
      </div>
    );
  }

  const metrics: readonly BannerMetric[] = [
    {
      key: 'hot_leads',
      label: t('crm.dashboard.morning_briefing.hot_leads'),
      count: briefing.hotLeads.length,
      color: 'var(--ant-color-error)',
      icon: Flame,
    },
    {
      key: 'overdue_tasks',
      label: t('crm.dashboard.morning_briefing.overdue_tasks'),
      count: briefing.overdueTasks.length,
      color: 'var(--ant-color-warning)',
      icon: Clock,
    },
    {
      key: 'meetings',
      label: t('crm.dashboard.morning_briefing.meetings'),
      count: briefing.todayMeetings.length,
      color: 'var(--ant-color-info)',
      icon: CalendarDays,
    },
    {
      key: 'risk_deals',
      label: t('crm.dashboard.morning_briefing.risk_deals'),
      count: briefing.riskDeals.length,
      color: 'var(--ant-color-warning)',
      icon: AlertTriangle,
    },
  ];

  if (collapsed) {
    return (
      <div
        data-testid="morning-briefing-banner"
        className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 crm-radius-m border px-3.5 py-2"
        style={{
          borderColor: 'var(--ant-color-border-secondary, #e7e5e4)',
          background: 'var(--ant-color-bg-container, #fffdfa)',
        }}
      >
        <span className="flex min-w-0 items-center gap-2">
          <Sparkles size={14} style={{ color: 'var(--ant-color-primary)' }} />
          <Text strong style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
            {t('crm.dashboard.morning_briefing.title')}
          </Text>
          <Text type="secondary" className="truncate" style={{ fontSize: 12 }}>
            {t('crm.dashboard.morning_briefing.subtitle')}
          </Text>
        </span>
        <span className="flex items-center gap-3">
          {metrics
            .filter((metric) => metric.count > 0)
            .map((metric) => (
              <span
                key={metric.key}
                className="flex items-center gap-1"
                style={{ fontSize: 11, color: metric.color, fontWeight: 550 }}
              >
                <metric.icon size={11} />
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{metric.count}</span>
              </span>
            ))}
          {onRunAi ? (
            <Button
              type="primary"
              size="small"
              icon={<Sparkles size={13} />}
              onClick={onRunAi}
              disabled={runAiDisabled}
              data-testid="morning-briefing-run-ai"
            >
              {t('crm.dashboard.morning_briefing.run_ai')}
            </Button>
          ) : null}
          <Button
            type="text"
            size="small"
            onClick={() => setCollapsed(false)}
            data-testid="morning-briefing-expand"
          >
            {t('crm.dashboard.morning_briefing.expand')}
          </Button>
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="morning-briefing-banner"
      className="crm-radius-m border px-3.5 py-2.5"
      style={{
        borderColor: 'var(--ant-color-border-secondary, #e7e5e4)',
        background: 'var(--ant-color-bg-container, #fffdfa)',
      }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles size={16} style={{ color: 'var(--ant-color-primary)', flexShrink: 0 }} />
          <Text strong style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
            {t('crm.dashboard.morning_briefing.title')}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }} className="truncate">
            {t('crm.dashboard.morning_briefing.subtitle')}
          </Text>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onRunAi ? (
            <Button
              type="primary"
              size="small"
              icon={<Sparkles size={13} />}
              onClick={onRunAi}
              disabled={runAiDisabled}
              data-testid="morning-briefing-run-ai"
            >
              {t('crm.dashboard.morning_briefing.run_ai')}
            </Button>
          ) : null}
          <Button type="text" size="small" onClick={() => setCollapsed(true)}>
            {t('crm.dashboard.morning_briefing.collapse')}
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-4 mt-1.5 flex-wrap">
        {metrics.map((metric) => (
          <span
            key={metric.key}
            className="flex items-center gap-1.5"
            style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}
          >
            <metric.icon size={12} style={{ color: metric.color }} />
            <span
              style={{
                fontWeight: 550,
                color: 'var(--ant-color-text)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {metric.count}
            </span>
            {metric.label}
          </span>
        ))}
        <span
          className="flex items-center gap-1.5"
          style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}
        >
          <span
            style={{
              fontWeight: 550,
              color: 'var(--ant-color-text)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {briefing.pipeline.conversionRate}%
          </span>
          {t('crm.dashboard.morning_briefing.conversion')}
        </span>
      </div>
    </div>
  );
}
